import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { navigate, pathForMarketing, resolveRoute } from '../utils/routes';

export type Language = 'ar' | 'en';

interface LanguageContextValue {
  language: Language;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  translationOverrides: Record<string, { ar: string; en: string }>;
  refreshTranslations: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'istihdaf_language';

/** لو المستخدم واقف على صفحة تسويقية (رئيسية/من نحن/الأسئلة الشائعة...)، اللغة بتتحدد
 *  من الرابط نفسه (بادئة /en) عشان تفضل ثابتة لمحركات البحث بغض النظر عن localStorage
 *  الجهاز. لباقي أنواع المسارات (التطبيق بعد تسجيل الدخول...) اللغة تفضيل شخصي محفوظ. */
function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'ar';
  const route = resolveRoute();
  if (route.kind === 'marketing' && route.language) return route.language;
  const stored = window.localStorage.getItem(STORAGE_KEY) as Language | null;
  if (stored === 'ar' || stored === 'en') return stored;
  return 'ar';
}

/** لو الصفحة الحالية تسويقية، بيحدّث الرابط ليطابق اللغة الجديدة (روابط /en منفصلة)
 *  بدل ما يفضل الرابط بلغة والمحتوى بلغة تانية. باقي الصفحات (التطبيق) مالهاش رابط
 *  مختلف حسب اللغة فمفيش داعي نلمس الرابط. */
function syncUrlWithLanguage(lang: Language): void {
  if (typeof window === 'undefined') return;
  const route = resolveRoute();
  if (route.kind !== 'marketing' || !route.marketingPage) return;
  navigate(pathForMarketing(route.marketingPage, lang), true);
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const [translationOverrides, setTranslationOverrides] = useState<Record<string, { ar: string; en: string }>>({});

  const refreshTranslations = useCallback(async () => {
    try {
      const response = await api.getTranslations();
      const next: Record<string, { ar: string; en: string }> = {};
      for (const row of response.translations || []) {
        next[row.translation_key] = { ar: row.text_ar, en: row.text_en };
      }
      setTranslationOverrides(next);
    } catch {
      // الترجمات المحلية في translations.ts شغّالة كبديل كامل —
      // لو السيرفر مش متاح أو الـ endpoint مش موجود، بنكمّل عادي بدون أي مشكلة.
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    refreshTranslations();
  }, [refreshTranslations]);

  // بيتابع تغييرات الرابط (روابط داخلية للصفحات التسويقية، أو زر رجوع/تقدّم
  // المتصفح) عشان اللغة تفضل متزامنة مع بادئة /en لو اتغيّر الرابط من غير ما يمر
  // بـ setLanguage/toggleLanguage (مثلاً ضغط على رابط تاني في الفوتر).
  useEffect(() => {
    const syncFromUrl = () => {
      const route = resolveRoute();
      if (route.kind === 'marketing' && route.language) {
        setLanguageState((prev) => (prev === route.language ? prev : route.language!));
      }
    };
    window.addEventListener('istihdaf:navigation', syncFromUrl);
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('istihdaf:navigation', syncFromUrl);
      window.removeEventListener('popstate', syncFromUrl);
    };
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    syncUrlWithLanguage(lang);
  };
  const toggleLanguage = () => {
    setLanguageState((prev) => {
      const next = prev === 'ar' ? 'en' : 'ar';
      syncUrlWithLanguage(next);
      return next;
    });
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, setLanguage, translationOverrides, refreshTranslations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
